import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Switch, Button, FormControl, InputLabel, Select, MenuItem, Alert,
  CircularProgress, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, Stack,
} from '@mui/material';
import { Add, Delete, Chat, Settings } from '@mui/icons-material';
import { chatAPI, settingsAPI } from '../../api/client';

const ROLES = [
  { value: 'tenant', label: 'Tenant (Student)' },
  { value: 'landlord', label: 'Landlord' },
  { value: 'agent', label: 'Agent' },
  { value: 'rent_control', label: 'Rent Control' },
  { value: 'super_admin', label: 'Super Admin' },
];

function AdminChatManagementPage() {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newPerm, setNewPerm] = useState({ from_role: '', to_role: '', is_allowed: true });
  const [chatSettings, setChatSettings] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(false);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const res = await chatAPI.getPermissions();
      setPermissions(res.data);
    } catch (err) {
      setError('Failed to load chat permissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchChatSettings = async () => {
    try {
      const res = await chatAPI.getSettings();
      setChatSettings(res.data || {});
    } catch (_) {}
  };

  useEffect(() => { fetchPermissions(); fetchChatSettings(); }, []);

  const toggleSetting = async (key) => {
    const current = chatSettings[key] === 'true';
    setSettingsLoading(true);
    try {
      await settingsAPI.update(key, { value: current ? 'false' : 'true' });
      setChatSettings(prev => ({ ...prev, [key]: current ? 'false' : 'true' }));
      setSuccess(`Setting "${key}" updated`);
    } catch (err) {
      setError('Failed to update setting');
    } finally { setSettingsLoading(false); }
  };

  const updateMaxSize = async (val) => {
    setSettingsLoading(true);
    try {
      await settingsAPI.update('chat_max_file_size_mb', { value: val });
      setChatSettings(prev => ({ ...prev, chat_max_file_size_mb: val }));
      setSuccess('Max file size updated');
    } catch (err) { setError('Failed to update'); }
    finally { setSettingsLoading(false); }
  };

  const handleToggle = async (perm) => {
    try {
      await chatAPI.updatePermission(perm.id, { is_allowed: !perm.is_allowed });
      setSuccess('Permission updated');
      fetchPermissions();
    } catch (err) {
      setError('Failed to update permission');
    }
  };

  const handleAdd = async () => {
    if (!newPerm.from_role || !newPerm.to_role) {
      setError('Please select both roles');
      return;
    }
    if (newPerm.from_role === newPerm.to_role) {
      setError('Roles must be different');
      return;
    }
    try {
      await chatAPI.createPermission(newPerm);
      setSuccess('Permission created');
      setDialogOpen(false);
      setNewPerm({ from_role: '', to_role: '', is_allowed: true });
      fetchPermissions();
    } catch (err) {
      setError('Failed to create permission');
    }
  };

  const handleDelete = async (id) => {
    try {
      await chatAPI.deletePermission(id);
      setSuccess('Permission deleted');
      fetchPermissions();
    } catch (err) {
      setError('Failed to delete permission');
    }
  };

  const getRoleLabel = (value) => ROLES.find(r => r.value === value)?.label || value;

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>Chat Management</Typography>

      {/* ── Global Chat Settings ── */}
      <Paper sx={{ p: 3, borderRadius: 3, mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <Settings color="primary" />
          <Typography variant="h6" fontWeight={600}>Chat Settings</Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography fontWeight={500}>Enable Chat for All Users</Typography>
              <Typography variant="body2" color="text.secondary">When enabled, all users can chat with each other regardless of role permissions below.</Typography>
            </Box>
            <Switch checked={chatSettings.chat_enabled === 'true'} onChange={() => toggleSetting('chat_enabled')} disabled={settingsLoading} color="primary" />
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography fontWeight={500}>Allow File Sharing</Typography>
              <Typography variant="body2" color="text.secondary">Allow users to share files, images, audio, and video in chat.</Typography>
            </Box>
            <Switch checked={chatSettings.chat_allow_file_sharing === 'true'} onChange={() => toggleSetting('chat_allow_file_sharing')} disabled={settingsLoading} />
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography fontWeight={500}>Max File Size (MB)</Typography>
              <Typography variant="body2" color="text.secondary">Maximum attachment size in megabytes.</Typography>
            </Box>
            <Select size="small" value={chatSettings.chat_max_file_size_mb || '10'}
              onChange={(e) => updateMaxSize(e.target.value)} disabled={settingsLoading} sx={{ minWidth: 80 }}>
              {[5, 10, 20, 50, 100].map(v => <MenuItem key={v} value={String(v)}>{v} MB</MenuItem>)}
            </Select>
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography fontWeight={500}>Enable Voice Calls</Typography>
              <Typography variant="body2" color="text.secondary">Allow voice calls between users.</Typography>
            </Box>
            <Switch checked={chatSettings.chat_allow_voice_calls === 'true'} onChange={() => toggleSetting('chat_allow_voice_calls')} disabled={settingsLoading} />
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography fontWeight={500}>Enable Video Calls</Typography>
              <Typography variant="body2" color="text.secondary">Allow video calls between users.</Typography>
            </Box>
            <Switch checked={chatSettings.chat_allow_video_calls === 'true'} onChange={() => toggleSetting('chat_allow_video_calls')} disabled={settingsLoading} />
          </Stack>
        </Stack>
      </Paper>

      {/* ── Role Permissions ── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chat color="primary" />
          <Typography variant="h6" fontWeight={600}>Chat Role Permissions</Typography>
        </Stack>
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
          Add Permission
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Configure which roles can communicate with each other. Super Admin can always chat with everyone.
        {chatSettings.chat_enabled === 'true' && <Chip label="Global chat is ON — all users can chat" color="success" size="small" sx={{ ml: 1 }} />}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>From Role</strong></TableCell>
              <TableCell><strong>To Role</strong></TableCell>
              <TableCell><strong>Allowed</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {permissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="text.secondary" py={3}>
                    No chat permissions configured yet. Add permissions to allow roles to chat with each other.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              permissions.map((perm) => (
                <TableRow key={perm.id}>
                  <TableCell><Chip label={getRoleLabel(perm.from_role)} size="small" /></TableCell>
                  <TableCell><Chip label={getRoleLabel(perm.to_role)} size="small" /></TableCell>
                  <TableCell>
                    <Switch checked={perm.is_allowed} onChange={() => handleToggle(perm)} color="primary" />
                  </TableCell>
                  <TableCell>
                    <IconButton color="error" onClick={() => handleDelete(perm.id)} size="small">
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Chat Permission</DialogTitle>
        <DialogContent>
          <Box mt={1} display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth>
              <InputLabel>From Role</InputLabel>
              <Select value={newPerm.from_role} label="From Role"
                onChange={(e) => setNewPerm({ ...newPerm, from_role: e.target.value })}>
                {ROLES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>To Role</InputLabel>
              <Select value={newPerm.to_role} label="To Role"
                onChange={(e) => setNewPerm({ ...newPerm, to_role: e.target.value })}>
                {ROLES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminChatManagementPage;
